import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listDriverApplications, listRestaurantApplications } from './adminApi';

function countByStatus(applications) {
  const counts = { pending: 0, approved: 0, rejected: 0, resubmission_requested: 0, total: applications.length };
  for (const app of applications) counts[app.status] = (counts[app.status] || 0) + 1;
  return counts;
}

function StatCard({ label, value, variant }) {
  return (
    <div className={`adm-stat-card adm-stat-card--${variant}`}>
      <div className="adm-stat-value">{value}</div>
      <div className="adm-stat-label">{label}</div>
    </div>
  );
}

function QueueSummary({ title, to, applications, loading, error, renderRow }) {
  const pending = applications.filter((a) => a.status === 'pending' || a.status === 'resubmission_requested');
  return (
    <div className="adm-detail-card" style={{ flex: 1, minWidth: 320 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h4 style={{ margin: 0 }}>{title}</h4>
        <Link to={to} className="adm-review-btn">View Queue</Link>
      </div>
      {loading ? (
        <p className="adm-section-note">Loading…</p>
      ) : error ? (
        <p className="adm-section-note" style={{ color: '#ff8080' }}>{error}</p>
      ) : pending.length === 0 ? (
        <p className="adm-section-note">Nothing waiting for review.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pending.slice(0, 5).map(renderRow)}
          {pending.length > 5 && (
            <p className="adm-section-note" style={{ margin: 0 }}>+{pending.length - 5} more pending</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminOverview({ secret }) {
  const [drivers, setDrivers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [loadingRestaurants, setLoadingRestaurants] = useState(true);
  const [driverError, setDriverError] = useState(null);
  const [restaurantError, setRestaurantError] = useState(null);

  useEffect(() => {
    listDriverApplications('all', secret)
      .then(setDrivers)
      .catch((err) => setDriverError(err.message))
      .finally(() => setLoadingDrivers(false));
    listRestaurantApplications('all', secret)
      .then(setRestaurants)
      .catch((err) => setRestaurantError(err.message))
      .finally(() => setLoadingRestaurants(false));
  }, [secret]);

  const driverCounts = countByStatus(drivers);
  const restaurantCounts = countByStatus(restaurants);
  const totalPending = (driverCounts.pending || 0) + (driverCounts.resubmission_requested || 0) + (restaurantCounts.pending || 0);
  const totalApproved = (driverCounts.approved || 0) + (restaurantCounts.approved || 0);
  const totalRejected = (driverCounts.rejected || 0) + (restaurantCounts.rejected || 0);
  const totalAll = driverCounts.total + restaurantCounts.total;

  return (
    <div>
      <div className="adm-page-header">
        <div>
          <h1 className="adm-page-title">Overview</h1>
          <p className="adm-page-subtitle">
            Live counts across both OrderUp partner review queues - every number here is a real application, not a mock.
          </p>
        </div>
      </div>

      <div className="adm-stats-grid">
        <StatCard label="Awaiting Review" value={totalPending} variant="pending" />
        <StatCard label="Approved" value={totalApproved} variant="approved" />
        <StatCard label="Rejected" value={totalRejected} variant="rejected" />
        <StatCard label="Total Applications" value={totalAll} variant="total" />
      </div>

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <QueueSummary
          title="🚗 Driver Applications"
          to="/admin/driver-applications"
          applications={drivers}
          loading={loadingDrivers}
          error={driverError}
          renderRow={(app) => (
            <div key={app.id} className="adm-detail-row">
              <span>{app.username}</span>
              <span className={`adm-status-pill adm-status-${app.status}`}>{app.status.replace('_', ' ')}</span>
            </div>
          )}
        />
        <QueueSummary
          title="🍽️ Restaurant Applications"
          to="/admin/restaurant-applications"
          applications={restaurants}
          loading={loadingRestaurants}
          error={restaurantError}
          renderRow={(app) => (
            <div key={app.id} className="adm-detail-row">
              <span>{app.businessName}</span>
              <span className={`adm-status-pill adm-status-${app.status}`}>{app.status.replace('_', ' ')}</span>
            </div>
          )}
        />
      </div>
    </div>
  );
}
